/**
 * Utilitário universal para resolução dinâmica de subdomínio/tenant no AesirShop.
 * Suporta execução no cliente (Browser) e no servidor (SSR / Next.js Server Components).
 */

export function getSubdomain(hostOrWindow?: string | null): string {
  const fallback = process.env.NEXT_PUBLIC_DEFAULT_TENANT || 'teste-loja';

  // 1. Permite override direto via query string no navegador (?subdomain=minhaloja ou ?loja=minhaloja)
  if (typeof window !== 'undefined' && window.location?.search) {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const querySub = urlParams.get('subdomain') || urlParams.get('tenant') || urlParams.get('loja');
      if (querySub && querySub.trim()) {
        return querySub.trim();
      }
    } catch {}
  }

  let host = '';
  if (typeof hostOrWindow === 'string' && hostOrWindow.trim()) {
    host = hostOrWindow.trim();
  } else if (typeof window !== 'undefined' && window.location?.host) {
    host = window.location.host;
  }

  if (!host) {
    return fallback;
  }

  // Remove porta se houver (ex: 148.113.167.2:3000 -> 148.113.167.2, localhost:3000 -> localhost)
  const hostWithoutPort = host.split(':')[0];

  // Tratamento de localhost, loopback ou porta de desenvolvimento
  if (
    hostWithoutPort === 'localhost' || 
    hostWithoutPort === '127.0.0.1' || 
    hostWithoutPort.startsWith('0.0.0.0') ||
    host.includes('localhost')
  ) {
    return fallback;
  }

  // Tratamento universal de endereços IPv4 diretos (ex: 148.113.167.2, 195.200.0.1)
  const isIpv4 = /^(\d{1,3}\.){3}\d{1,3}$/.test(hostWithoutPort);
  if (isIpv4) {
    return fallback;
  }

  // Domínio de homologação / desenvolvimento interno raiz (sem subdomínio de loja específico)
  if (
    hostWithoutPort.startsWith('dev.aesir.shop') || 
    hostWithoutPort.startsWith('homolog.aesir.shop') || 
    hostWithoutPort === 'aesir.shop' || 
    hostWithoutPort === 'www.aesir.shop'
  ) {
    return fallback;
  }

  // Resolução de subdomínios sob *.aesir.shop (ex: teste-loja.dev.aesir.shop, teste-loja.homolog.aesir.shop, bodyfuel.aesir.shop)
  const parts = hostWithoutPort.split('.');
  if (parts.length > 2 && hostWithoutPort.includes('aesir.shop')) {
    const sub = parts[0];
    const reserved = ['www', 'dev', 'homolog', 'api', 'admin'];
    if (!reserved.includes(sub)) {
      return sub;
    }
  }

  // Caso seja um subdomínio genérico em qualquer outro domínio (ex: loja.meudominio.com.br)
  if (parts.length >= 3 && parts[0] !== 'www') {
    return parts[0];
  }

  return fallback;
}
